// Scratchpad: admin-only brainstorming blocks stored beside a script.
//
// The whole scratchpad lives in `scripts.scratch_json` as one JSON document.
// Nothing in here is ever selected into a client payload (see client.ts and
// the "no scratch keys" test). Blocks are a record of thinking, not a link:
// promoting a block into the script copies its text and stamps `used_at`,
// after which the two copies never touch again.

export type ScratchBlockType = 'idea' | 'hook' | 'structure' | 'line' | 'question' | 'paste';

export const SCRATCH_BLOCK_TYPES: readonly ScratchBlockType[] = [
  'idea',
  'hook',
  'structure',
  'line',
  'question',
  'paste',
];

export const SCRATCH_TYPE_LABEL: Record<ScratchBlockType, string> = {
  idea: 'Idea',
  hook: 'Hook',
  structure: 'Structure',
  line: 'Line',
  question: 'Question',
  paste: 'Paste',
};

export const DEFAULT_SCRATCH_BLOCK_TYPE: ScratchBlockType = 'idea';
export const SCRATCH_MAX_BLOCKS = 500;

export interface ScratchBlock {
  /** ULID, stable for the block's life. */
  id: string;
  type: ScratchBlockType;
  /** ProseMirror doc JSON (subset: paragraph, heading, lists, bold/italic). */
  body: unknown;
  /** Epoch ms when the block was promoted into the script; null until then. */
  used_at: number | null;
  created_at: number;
}

export interface ScratchDoc {
  version: 1;
  blocks: ScratchBlock[];
}

export const EMPTY_SCRATCH: ScratchDoc = { version: 1, blocks: [] };

export function isScratchBlockType(value: unknown): value is ScratchBlockType {
  return typeof value === 'string' && (SCRATCH_BLOCK_TYPES as readonly string[]).includes(value);
}

/**
 * Defensive parse of a stored `scratch_json` string. Never throws: a null,
 * empty, malformed or foreign-shaped value yields an empty scratchpad, and
 * individual blocks that fail validation are dropped rather than crashing the
 * editor. Mirrors the tolerance of `parseFeatures`.
 */
export function parseScratch(json: string | null | undefined): ScratchDoc {
  if (!json) return { version: 1, blocks: [] };
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return { version: 1, blocks: [] };
  }
  if (!raw || typeof raw !== 'object') return { version: 1, blocks: [] };
  const blocksRaw = (raw as { blocks?: unknown }).blocks;
  if (!Array.isArray(blocksRaw)) return { version: 1, blocks: [] };
  const blocks: ScratchBlock[] = [];
  const seenIds = new Set<string>();
  for (const b of blocksRaw) {
    if (!b || typeof b !== 'object') continue;
    const o = b as Record<string, unknown>;
    if (typeof o.id !== 'string' || !o.id) continue;
    if (seenIds.has(o.id)) continue;
    seenIds.add(o.id);
    blocks.push({
      id: o.id,
      type: isScratchBlockType(o.type) ? o.type : DEFAULT_SCRATCH_BLOCK_TYPE,
      body: o.body ?? null,
      used_at: typeof o.used_at === 'number' ? o.used_at : null,
      created_at: typeof o.created_at === 'number' ? o.created_at : 0,
    });
  }
  return { version: 1, blocks };
}

export function serializeScratch(doc: ScratchDoc): string {
  return JSON.stringify({ version: 1, blocks: doc.blocks });
}
